import React, { SyntheticEvent, useEffect, useState } from "react";
import { TransitionGroup } from "react-transition-group";

import { useSelector } from "react-redux";
import { UseAppDispatch } from "redux/store";

import {
  changeMaxDurations,
  filterSelector,
  IMaxDurations,
} from "redux/filters";

import { useTranslations } from "hooks/useTranslations";
import { IDurationFilter } from "hooks/translations.interfaces";
import { searchQuerySelector } from "redux/searchQuery";
import {
  deepCloneObject,
  isDiff,
  pushToDataLayer,
} from "@thekilroygroup/shared-ui";
import {
  Box,
  Collapse,
  Slider,
  SxProps,
  Typography,
  useTheme,
} from "@mui/material";

export default function DurationFilter() {
  const dispatch = UseAppDispatch();

  const { flights, tripType } = useSelector(searchQuerySelector);
  const { maxDurations } = useSelector(filterSelector);
  const durations = maxDurations.maxDurations;

  const { translations } = useTranslations();
  const translationsDurationFilter: IDurationFilter = deepCloneObject(
    translations.filters.filterTypes.filter((obj) => {
      return obj.filter === "maxDurations";
    })[0],
  );

  const { transitions } = useTheme();

  const translationsFilters = translations.filters;

  useEffect(() => {
    updateFilterAmount();
  }, [flights.length, tripType]);

  const [durationsState, setDurationsState] = useState(
    maxDurations.maxDurations,
  );

  useEffect(() => {
    setDurationsState(durations);
  }, [maxDurations]);

  const updateFilterAmount = () => {
    let nextMaxDurations: IMaxDurations = deepCloneObject(maxDurations);
    let nextDurations: number[] = nextMaxDurations.maxDurations;

    if (tripType === "ONE_WAY" && nextDurations.length > 1) {
      nextDurations.length = 1;
    } else if (tripType === "RETURN") {
      nextDurations.length = 2;
      if (typeof nextDurations[1] === "undefined") {
        nextDurations[1] = Number(translationsFilters.filterValues.maxDuration);
      }
    } else {
      nextDurations.length = flights.length;
      flights.forEach((flight, index) => {
        if (typeof nextDurations[index] === "undefined") {
          nextDurations[index] = Number(
            translationsFilters.filterValues.maxDuration,
          );
        }
      });
    }
    nextMaxDurations.maxDurations = nextDurations;

    if (isDiff(nextMaxDurations, maxDurations)) {
      setDurationsState(nextMaxDurations.maxDurations);
      dispatch(changeMaxDurations(nextMaxDurations));
    }
  };

  const handleChangeSlider = (
    event: Event,
    newValue: number | number[],
    filterIndex: number,
  ) => {
    if (typeof newValue === "number") {
      let nextMaxDurations: number[] = deepCloneObject(durationsState);
      nextMaxDurations[filterIndex] = newValue;
      setDurationsState(nextMaxDurations);
    }
  };

  const handleChangeSliderComitted = (
    event: Event | SyntheticEvent<Element, Event>,
    newValue: number | number[],
    filterIndex: number,
  ) => {
    let isTouched = false;
    durationsState.forEach((duration, index) => {
      if (duration !== Number(translationsFilters.filterValues.maxDuration)) {
        isTouched = true;
      }
    });

    const nextMaxDurations: IMaxDurations = deepCloneObject(maxDurations);

    if (nextMaxDurations.touched !== isTouched) {
      nextMaxDurations.touched = isTouched;
    }
    if (typeof newValue === "number") {
      const duration = `${newValue / 60}`;
      pushToDataLayer(`Max duration: ${duration}`, "Filter - Duration");
      nextMaxDurations.maxDurations[filterIndex] = newValue;
      dispatch(changeMaxDurations(nextMaxDurations));
    }
  };

  const getFlightIndexHeader = (index: number) => {
    const header =
      tripType === "MULTI_DEST"
        ? `${index + 1}. ${translationsDurationFilter.multiFlightText}`
        : `${
            index === 0
              ? translationsDurationFilter.outboundText
              : translationsDurationFilter.returnText
          }`;
    return (
      <Typography variant="titleSmall" weight="bold">
        {header}
      </Typography>
    );
  };

  const getMarks = (index: number) => {
    const value = durationsState[index];
    return [
      {
        value: 0,
        label: 0,
      },
      {
        value: Number(translationsFilters.filterValues.maxDuration),
        label: `${value / 60} ${translationsDurationFilter.hours}`,
      },
    ];
  };

  return (
    <>
      <TransitionGroup>
        {durationsState.map((duration, index) => {
          return (
            <Collapse
              key={`transition-${index}`}
              in={true}
              easing={transitions.easing.easeIn}
              timeout={transitions.duration.shortest}
              sx={sxStyles.collapse}
            >
              <Box sx={sxStyles.filter} key={`departure-time-filter-${index}`}>
                {getFlightIndexHeader(index)}

                <Box sx={sxStyles.rangeSlider}>
                  <Slider
                    getAriaValueText={(value: number) => {
                      return String(value);
                    }}
                    onChange={(event: Event, newValue: number | number[]) =>
                      handleChangeSlider(event, newValue, index)
                    }
                    onChangeCommitted={(
                      event: Event | SyntheticEvent<Element, Event>,
                      newValue: number | number[],
                    ) => handleChangeSliderComitted(event, newValue, index)}
                    value={Number(duration)}
                    min={0}
                    max={Number(translationsFilters.filterValues.maxDuration)}
                    step={60}
                    valueLabelDisplay="auto"
                    marks={getMarks(index)}
                    valueLabelFormat={(value: number) => {
                      return `${value / 60} ${
                        translationsDurationFilter.hours
                      }`;
                    }}
                  />
                </Box>
              </Box>
            </Collapse>
          );
        })}
      </TransitionGroup>
    </>
  );
}

const sxStyles: { [key: string]: SxProps } = {
  collapse: {
    borderBottom: "1px solid",
    borderBottomColor: "grey.300",
    "&:last-of-type": {
      borderBottom: "0px solid",
    },
  },
  filter: {
    my: 1,
    mx: 0,
    py: 1,
    px: 0,
  },
  rangeSlider: {
    borderBottom: "1px solid",
    borderBottomColor: "grey.200",
    "&:last-of-type": {
      borderBottom: "0px solid",
    },
    "& .MuiSlider-root": {
      color: "grey.600",
    },
    "& .MuiSlider-rail": {
      color: "grey.400",
      p: "2px 0 0 0",
    },
    "& .MuiSlider-thumb": {
      backgroundColor: "white.main",
    },
    "& .MuiSlider-valueLabel": {
      backgroundColor: "primary.main",
      py: 0.35,
      px: 0.75,
    },
    "& .MuiSlider-mark": {
      display: "none",
    },
    "& .MuiSlider-markLabel": {
      "&[data-index='0']": {
        transform: "translateX(0)",
      },
      "&[data-index='1']": {
        transform: "translateX(-100%)",
      },
    },
  },
};
